import { useState } from "react";
import { FaEdit, FaLock, FaPlus, FaTrash } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  getPatientStatuses,
  deletePatientStatus,
  getAllStatusOptions,
} from "../../services/patient/settings";
import Table from "../table";
import styles from "../../styles/patient-page-modules/PatientSettings.module.css";
import Spinner from "../spinner";
import Reload from "../ui/reload-data";
import Button from "../button-v1";
import AlertDialouge from "../../components/alert-dialogue";
import StatusModal from "./add-status";
import { Badge } from "../ui/badge";

const Statuses = ({ isOpen, toggleSection }) => {
  const patientId = sessionStorage.getItem("_pt_id");
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusId, setStatusId] = useState(null);
  const [isDeletealertOpen, setIsDeleteAlertOpen] = useState(false);

  const {
    data: availableStatuses,
    isLoading: isLoadingGlobalStatuses,
    error: optionsError,
  } = useQuery(["global-statuses"], () => getAllStatusOptions(), {
    onError: (err) => {
      console.error("Could not load statuses:", err);
      toast.error("Could not load status. Please try again.");
    },
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: patientStatuses,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ["patient-statuses", patientId],
    () => getPatientStatuses(patientId),
    {
      enabled: !!patientId,
      onError: (err) => {
        console.error("Could not load statuses:", err);
        toast.error("Could not load patient statuses. Please try again.");
      },
    }
  );

  const deleteStatusMutation = useMutation(
    () => deletePatientStatus(statusId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["patient-statuses", patientId]);
        setIsDeleteAlertOpen(false);
        toast.success("Status remove successfully");
      },
      onError: (error) => {
        console.error("Could not delete status:", error);
        toast.error("Could not remove status. Please try again.");
      },
    }
  );

  const handleEdit = (id) => {
    if (!id) return;

    setStatusId(id);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (!id) return;

    setStatusId(id);
    setIsDeleteAlertOpen(true);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handle reopening modal after admin status creation
  const handleReopenModal = () => {
    setIsModalOpen(true);
  };

  const columns = [
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      render: (row) =>
        availableStatuses?.find((_status) => _status?.id == row?.status)?.name,
    },
    {
      key: "description",
      label: "Description",
      alignLeft: true,
      sorting: false,
      render: (row) => row?.description || "_ _",
    },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (row) => (
        <div className={styles.actionContainer}>
          {row?.is_default ? (
            <Button
              variant="ghost"
              // size="small"
              // onClick={(e) => {
              //   e.stopPropagation();
              //   handleEdit(row.id);
              // }}
              // style={{
              //   background: "transparent",
              //   border: "none",
              //   cursor: "pointer",
              // }}
              // disabled={deleteStatusMutation.isLoading}
            >
              <FaLock color="black" />
            </Button>
          ) : (
            <Button
              variant="normal"
              // size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row?.id);
              }}
              // style={{
              //   background: "transparent",
              //   border: "none",
              //   cursor: "pointer",
              // }}
              disabled={deleteStatusMutation?.isLoading}
            >
              <FaEdit color="black" />
            </Button>
          )}
          <Button
            variant="ghost"
            // size="normal"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(row?.id);
            }}
            // style={{
            //   background: "transparent",
            //   border: "none",
            //   cursor: "pointer",
            // }}
            disabled={deleteStatusMutation?.isLoading}
          >
            <FaTrash color="black" />
          </Button>
        </div>
      ),
    },
  ];

  const rows =
    patientStatuses
      ?.map((status) => ({
        id: status?.id,
        patient: status?.patient,
        status: status?.status,
        description: status?.description,
        is_default: availableStatuses?.find(
          (_status) => _status?.description === status?.description
        )?.is_default,
      }))
      ?.sort((a, b) => {
        // true comes first, so b - a
        return (b?.is_default === true) - (a?.is_default === true);
      }) || [];

  return (
    <div className={`${styles.form_section} ${isOpen ? "" : styles.close}`}>
      <div
        className={styles.section_header}
        onClick={() => toggleSection("sec3")}
      >
        <div className={styles.section_number}>4</div>
        <div>
          <p className={styles.section_heading}>Statuses</p>
          <p className={styles.sub_heading}>Profile Statuses</p>
        </div>
        <div className={styles.drop_icon}>
          <FontAwesomeIcon icon={faChevronDown} />
        </div>
      </div>

      <div className={`${styles.input_fields} ${styles.status_main_cont}`}>
        <div className={styles.statuses_cont}>
          <p className={styles.section_heading}>Statuses</p>
          <Button onClick={handleOpenModal}>
            <FaPlus size="10px" /> Assign Status
          </Button>
        </div>

        <div className={styles.status_cont}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <Spinner />
            </div>
          ) : error ? (
            <Reload
              onReload={refetch}
              message="Could not load patient statuses"
            />
          ) : (
            <Table
              columns={columns}
              data={rows}
              loading={false}
              isRowClickable={false}
              height={400}
              rowColor={(row) => row?.is_default && "var(--light-blue)"}
            />
          )}
        </div>
      </div>

      <StatusModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        statusId={statusId}
        setStatusId={setStatusId}
        onReopenModal={handleReopenModal}
      />

      {/* Delete Alert */}
      <AlertDialouge
        open={isDeletealertOpen}
        setOpen={setIsDeleteAlertOpen}
        title="Delete Status"
        description="Are you sure you want to delete this status?"
      >
        <Button variant="normal" onClick={() => setIsDeleteAlertOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={() => deleteStatusMutation?.mutate()}
          loading={deleteStatusMutation?.isLoading}
        >
          Delete
        </Button>
      </AlertDialouge>
    </div>
  );
};

export default Statuses;
